import React from 'react';
import {
  Button,
  Grid,
  Typography,
  useTheme,
  Box,
  Stack,
  Paper,
  Divider,
  Chip,
} from '@mui/material';
import startCase from 'lodash/startCase';
import moment from 'moment';
import { moneyPrint } from '@pv/common/utils';
import { ExternalLink, Calendar, Clock } from 'react-feather';

export interface Subscription {
  id: string;
  status?: string | null;
  planName: string;
  stripeSubscriptionId?: string | null;
  stripeScheduleId?: string | null;
  billingInterval: string;
  discount?: number;
  monthlyRate?: number;
  startDate?: string | null;
  endDate?: string | null;
  createdAt: string;
  updatedAt: string;
  notes?: string | null;
}

const formatDate = (date?: string) => {
  if (!date) return '';
  return moment(date).format('MMMM DD, YYYY');
};

export const SubscriptionItem = ({
  subscription,
  header,
}: {
  subscription: Subscription;
  header: string;
}) => {
  const theme = useTheme();
  const stripeSubscriptionId =
    subscription.stripeSubscriptionId || subscription.stripeScheduleId;

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'success';
      case 'canceled':
      case 'cancelled':
        return 'error';
      case 'past_due':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
      <Stack spacing={3}>
        {header && (
          <Box>
            <Typography variant="h5" fontWeight="600" color="primary">
              {header}
            </Typography>
            <Divider sx={{ mt: 1 }} />
          </Box>
        )}

        <Grid container spacing={3}>
          {/* Plan Information */}
          <Grid item xs={12} md={6}>
            <Stack spacing={2}>
              <Box>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  PLAN DETAILS
                </Typography>
                <Typography variant="h6" fontWeight="600">
                  {subscription.planName}
                </Typography>
                <Chip
                  label={startCase(subscription.status || '')}
                  color={getStatusColor(subscription.status || '') as any}
                  size="small"
                  sx={{ mt: 1, p: 1 }}
                />
              </Box>

              <Box>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  BILLING
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography>
                    {moneyPrint((subscription.monthlyRate || 0) / 100)} / month
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {moneyPrint(((subscription.monthlyRate || 0) / 100) * 12)}{' '}
                  annually
                </Typography>
                {!!subscription.discount && subscription.discount > 0.0 && (
                  <Typography variant="body2" color="success.main">
                    {Math.round(subscription.discount * 100)}% discount applied
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  {startCase(subscription.billingInterval)} billing cycle
                </Typography>
              </Box>
            </Stack>
          </Grid>

          {/* Dates and IDs */}
          <Grid item xs={12} md={6}>
            <Stack spacing={2}>
              <Box>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  TIMELINE
                </Typography>
                <Stack spacing={1}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Calendar size={16} />
                    <Typography variant="body2">
                      <strong>Start:</strong>{' '}
                      {subscription.startDate
                        ? formatDate(subscription.startDate)
                        : 'none'}
                    </Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Clock size={16} />
                    <Typography variant="body2">
                      <strong>End:</strong>{' '}
                      {subscription.endDate
                        ? formatDate(subscription.endDate)
                        : 'none'}
                    </Typography>
                  </Stack>
                </Stack>
              </Box>

              <Box>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  TECHNICAL DETAILS
                </Typography>
                <Typography
                  variant="body2"
                  fontFamily="monospace"
                  color="text.secondary"
                >
                  Subscription Plan ID: {subscription.id}
                </Typography>
                <Typography
                  variant="body2"
                  fontFamily="monospace"
                  color="text.secondary"
                >
                  Created: {formatDate(subscription.createdAt)}
                </Typography>
                <Typography
                  variant="body2"
                  fontFamily="monospace"
                  color="text.secondary"
                >
                  Updated: {formatDate(subscription.updatedAt)}
                </Typography>
              </Box>
            </Stack>
          </Grid>
        </Grid>

        {stripeSubscriptionId && (
          <Box sx={{ pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
            <Button
              size="medium"
              variant="outlined"
              target="_blank"
              href={`https://dashboard.stripe.com/subscriptions/${stripeSubscriptionId}`}
              startIcon={<ExternalLink size={16} />}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              View in Stripe Dashboard
            </Button>
          </Box>
        )}
      </Stack>
    </Paper>
  );
};
